/**
 * Excel writer.
 * All Office.js WRITE operations are centralized here.
 * No other file in the addin may call Excel.run() for writes.
 *
 * Error handling strategy:
 *   - Every public function wraps Excel.run() in try/catch.
 *   - OfficeExtension.Error is caught and translated to a user-facing message.
 *   - Input is validated before calling Office.js (fail fast).
 *   - Thrown errors are always plain Error objects with French messages,
 *     ready for display in the UI via ErrorDisplay.
 */

/* global Excel, OfficeExtension */

type WriterUndoEntry =
  | {
      kind: 'cell_restore';
      sheetName: string;
      address: string;
      previousFormula: string;
      previousValue: unknown;
    }
  | {
      kind: 'range_restore';
      sheetName: string;
      address: string;
      previousFormulas: unknown[][];
      previousValues: unknown[][];
    }
  | {
      kind: 'fill_restore';
      sheetName: string;
      address: string;
      previousColor: string | null;
    };

const writerUndoStack: WriterUndoEntry[] = [];

function pushUndoEntry(entry: WriterUndoEntry): void {
  writerUndoStack.push(entry);
  if (writerUndoStack.length > 50) {
    writerUndoStack.shift();
  }
}

function isFormulaNameError(text: string): boolean {
  const upper = text.toUpperCase();
  return upper.includes('#NAME?') || upper.includes('#NOM?');
}

function normalizeAddress(address: string): string {
  return address.includes('!') ? address.split('!').slice(1).join('!') : address;
}

function validateFormulaForInsertion(formula: string): string {
  if (!formula || !formula.trim()) {
    throw new Error('Impossible d\'inserer une formule vide.');
  }

  const normalized = formula.trim();
  if (!normalized.startsWith('=')) {
    throw new Error('La formule doit commencer par le signe =.');
  }
  if (normalized.startsWith('==')) {
    throw new Error('Formule invalide: "==" n\'est pas un debut de formule valide.');
  }
  if (normalized.length < 2) {
    throw new Error('Formule invalide: contenu manquant apres "=".');
  }
  if (/[\r\n\t]/.test(normalized)) {
    throw new Error('Formule invalide: caracteres de controle non autorises.');
  }
  if (normalized.length > 8192) {
    throw new Error('Formule trop longue pour une insertion securisee.');
  }

  // Basic parenthesis validation (fast guard before Excel evaluation).
  let openParens = 0;
  for (const ch of normalized) {
    if (ch === '(') openParens++;
    if (ch === ')') openParens--;
    if (openParens < 0) {
      throw new Error('Formule invalide: parentheses non equilibrees.');
    }
  }
  if (openParens !== 0) {
    throw new Error('Formule invalide: parentheses non equilibrees.');
  }

  return normalized;
}

function buildRestoreMatrix(
  previousFormulas: unknown[][],
  previousValues: unknown[][],
): unknown[][] {
  const rowCount = Math.max(previousFormulas.length, previousValues.length);
  const restored: unknown[][] = [];
  for (let r = 0; r < rowCount; r++) {
    const formulaRow = previousFormulas[r] ?? [];
    const valueRow = previousValues[r] ?? [];
    const colCount = Math.max(formulaRow.length, valueRow.length);
    const outRow: unknown[] = [];
    for (let c = 0; c < colCount; c++) {
      const f = formulaRow[c];
      const v = valueRow[c];
      outRow.push(typeof f === 'string' && f.startsWith('=') ? f : v);
    }
    restored.push(outRow);
  }
  return restored;
}

/**
 * Insert a formula into the currently active cell.
 * The formula MUST start with = (e.g. "=SUM(A1:A10)" or "=SOMME(A1:A10)").
 *
 * @param formula  The formula string starting with =
 * @param options  Set `local: true` to write via `formulasLocal` (for
 *                 localized function names like SOMME, RECHERCHEV).
 */
export async function insertFormula(
  formula: string,
  options?: { local?: boolean },
  address?: string,
): Promise<void> {
  const normalized = validateFormulaForInsertion(formula);

  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');
      const range = address ? sheet.getRange(address) : ctx.workbook.getSelectedRange();
      range.load('cellCount');
      await ctx.sync();

      if (range.cellCount === 0) {
        throw new Error('Aucune cellule selectionnee. Selectionnez une cellule puis reessayez.');
      }

      const cell = range.getCell(0, 0);
      cell.load(['address', 'formulas', 'values']);
      await ctx.sync();
      const previousFormula = String(cell.formulas?.[0]?.[0] ?? '');
      const previousValue = cell.values?.[0]?.[0];

      if (options?.local === true) {
        cell.formulasLocal = [[normalized]];
        await ctx.sync();
        pushUndoEntry({
          kind: 'cell_restore',
          sheetName: sheet.name,
          address: normalizeAddress(cell.address),
          previousFormula,
          previousValue,
        });
        return;
      }
      if (options?.local === false) {
        cell.formulas = [[normalized]];
        await ctx.sync();
        pushUndoEntry({
          kind: 'cell_restore',
          sheetName: sheet.name,
          address: normalizeAddress(cell.address),
          previousFormula,
          previousValue,
        });
        return;
      }

      // Auto mode: try international formula first, then fallback to localized formula
      // if Excel stores a function-name error (#NAME? / #NOM?).
      try {
        cell.formulas = [[normalized]];
        await ctx.sync();
        cell.load('text');
        await ctx.sync();
        const rendered = String(cell.text?.[0]?.[0] ?? '');
        if (isFormulaNameError(rendered)) {
          cell.formulasLocal = [[normalized]];
          await ctx.sync();
        }
      } catch {
        cell.formulasLocal = [[normalized]];
        await ctx.sync();
      }

      pushUndoEntry({
        kind: 'cell_restore',
        sheetName: sheet.name,
        address: normalizeAddress(cell.address),
        previousFormula,
        previousValue,
      });
    });
  } catch (err) {
    throw toWriterError(err, 'inserer la formule');
  }
}

/**
 * Insert a scalar value into the currently active cell.
 */
export async function insertValue(value: string | number, address?: string): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');
      const range = address ? sheet.getRange(address) : ctx.workbook.getSelectedRange();
      range.load('cellCount');
      await ctx.sync();

      if (range.cellCount === 0) {
        throw new Error('Aucune cellule selectionnee. Selectionnez une cellule puis reessayez.');
      }

      const cell = range.getCell(0, 0);
      cell.load(['address', 'formulas', 'values']);
      await ctx.sync();
      const previousFormula = String(cell.formulas?.[0]?.[0] ?? '');
      const previousValue = cell.values?.[0]?.[0];
      cell.values = [[value]];
      await ctx.sync();
      pushUndoEntry({
        kind: 'cell_restore',
        sheetName: sheet.name,
        address: normalizeAddress(cell.address),
        previousFormula,
        previousValue,
      });
    });
  } catch (err) {
    throw toWriterError(err, 'inserer la valeur');
  }
}

/**
 * Undo the last AI write action tracked by the writer.
 */
export async function undoLastWriterAction(): Promise<boolean> {
  const entry = writerUndoStack.pop();
  if (!entry) {
    return false;
  }

  await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItem(entry.sheetName);
    if (entry.kind === 'cell_restore') {
      const cell = sheet.getRange(entry.address);
      if (entry.previousFormula && entry.previousFormula.startsWith('=')) {
        cell.formulas = [[entry.previousFormula]];
      } else {
        cell.values = [[entry.previousValue ?? '']];
      }
    } else if (entry.kind === 'range_restore') {
      const range = sheet.getRange(entry.address);
      const restored = buildRestoreMatrix(entry.previousFormulas, entry.previousValues);
      if (restored.length > 0) {
        range.formulas = restored as (string | boolean | number)[][];
      }
    } else if (entry.kind === 'fill_restore') {
      const range = sheet.getRange(entry.address);
      if (entry.previousColor && entry.previousColor.trim()) {
        range.format.fill.color = entry.previousColor;
      } else {
        range.format.fill.clear();
      }
    }
    await ctx.sync();
  });

  return true;
}

/**
 * Write a 2D array of values starting at the selected cell.
 */
export async function writeValues(values: unknown[][]): Promise<void> {
  if (values.length === 0) return;

  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');
      const range = ctx.workbook.getSelectedRange();
      range.load('cellCount');
      await ctx.sync();

      if (range.cellCount === 0) {
        throw new Error('Aucune cellule selectionnee. Selectionnez une cellule puis reessayez.');
      }

      const startCell = range.getCell(0, 0);
      const targetRange = startCell.getResizedRange(
        values.length - 1,
        (values[0]?.length ?? 1) - 1,
      );
      targetRange.load(['address', 'formulas', 'values']);
      await ctx.sync();
      const previousFormulas = (targetRange.formulas as unknown[][]) ?? [];
      const previousValues = (targetRange.values as unknown[][]) ?? [];

      targetRange.values = values;
      await ctx.sync();
      pushUndoEntry({
        kind: 'range_restore',
        sheetName: sheet.name,
        address: normalizeAddress(targetRange.address),
        previousFormulas,
        previousValues,
      });
    });
  } catch (err) {
    throw toWriterError(err, 'ecrire les valeurs');
  }
}

/**
 * Write a 2D array of values into a column adjacent to the current selection.
 * Writes to the column(s) immediately to the right of the selected range.
 */
export async function writeValuesAdjacent(values: unknown[][]): Promise<void> {
  if (values.length === 0) return;

  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');
      const range = ctx.workbook.getSelectedRange();
      range.load(['cellCount', 'columnCount']);
      await ctx.sync();

      if (range.cellCount === 0) {
        throw new Error('Aucune cellule selectionnee. Selectionnez une cellule puis reessayez.');
      }

      // Get the cell just to the right of the selection's top-left corner
      const startCell = range.getCell(0, 0);
      const adjacentStart = startCell.getOffsetRange(0, range.columnCount);
      const targetRange = adjacentStart.getResizedRange(
        values.length - 1,
        (values[0]?.length ?? 1) - 1,
      );
      targetRange.load(['address', 'formulas', 'values']);
      await ctx.sync();
      const previousFormulas = (targetRange.formulas as unknown[][]) ?? [];
      const previousValues = (targetRange.values as unknown[][]) ?? [];

      targetRange.values = values;
      await ctx.sync();
      pushUndoEntry({
        kind: 'range_restore',
        sheetName: sheet.name,
        address: normalizeAddress(targetRange.address),
        previousFormulas,
        previousValues,
      });
    });
  } catch (err) {
    throw toWriterError(err, 'ecrire les valeurs dans la colonne adjacente');
  }
}

/**
 * Temporarily highlight a range in Excel with a colored background.
 * Useful for showing which cells will be affected before an action.
 *
 * @param address  The range address (e.g. "A1:C10")
 * @param color    Fill color hex (default: yellow highlight)
 */
export async function highlightRange(
  address: string,
  color = '#FFF3CD',
): Promise<void> {
  if (!address) return;

  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');
      const range = sheet.getRange(address);
      range.load('address');
      range.format.fill.load('color');
      await ctx.sync();
      const previousColor = range.format.fill.color ? String(range.format.fill.color) : null;
      range.format.fill.color = color;
      range.select();
      await ctx.sync();
      pushUndoEntry({
        kind: 'fill_restore',
        sheetName: sheet.name,
        address: normalizeAddress(range.address),
        previousColor,
      });
    });
  } catch (err) {
    throw toWriterError(err, 'surligner la plage');
  }
}

/**
 * Clear the fill color from a range (remove highlight).
 */
export async function clearHighlight(address: string): Promise<void> {
  if (!address) return;

  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');
      const range = sheet.getRange(address);
      range.load('address');
      range.format.fill.load('color');
      await ctx.sync();
      const previousColor = range.format.fill.color ? String(range.format.fill.color) : null;
      range.format.fill.clear();
      await ctx.sync();
      pushUndoEntry({
        kind: 'fill_restore',
        sheetName: sheet.name,
        address: normalizeAddress(range.address),
        previousColor,
      });
    });
  } catch (err) {
    throw toWriterError(err, 'effacer le surlignage');
  }
}

/**
 * Create an Excel chart from the current selection.
 *
 * @param chartType  Excel chart type string (e.g. "ColumnClustered", "Line", "Pie")
 * @param title      Chart title
 * @param address    Optional source range address. Uses current selection if omitted.
 */
export async function createChart(
  chartType: string,
  title: string,
  address?: string,
): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();

      // Resolve source range — strip sheet name prefix if present
      let sourceRange: Excel.Range;
      if (address) {
        const rangeOnly = address.includes('!') ? address.split('!').slice(1).join('!') : address;
        sourceRange = sheet.getRange(rangeOnly);
      } else {
        sourceRange = ctx.workbook.getSelectedRange();
      }

      // Load range dimensions to validate before chart creation
      sourceRange.load(['rowCount', 'columnCount', 'address']);
      await ctx.sync();

      if (sourceRange.rowCount < 2 || sourceRange.columnCount < 1) {
        throw new Error(
          'La selection doit contenir au moins 2 lignes et 1 colonne pour creer un graphique.',
        );
      }

      // Create the chart
      const chart = sheet.charts.add(
        chartType as Excel.ChartType,
        sourceRange,
        Excel.ChartSeriesBy.auto,
      );

      // Set title
      chart.title.visible = true;
      chart.title.text = title;

      // Position the chart to the right of the data, on the same row
      const startCol = sourceRange.columnCount + 1;
      const posCell = sourceRange.getCell(0, 0).getOffsetRange(0, startCol);
      chart.setPosition(posCell);

      await ctx.sync();
    });
  } catch (err) {
    throw toWriterError(err, 'creer le graphique');
  }
}

/**
 * Write pre-filtered chart data to a temporary area on a new (hidden) sheet,
 * create a chart from it, then move the chart to the active sheet.
 *
 * This enables "smart" chart commands like:
 *   "graphique repartition hommes femmes salaire > 5000"
 * where the backend pre-filters and aggregates the data.
 */
export async function writeFilteredDataAndChart(
  data: unknown[][],
  chartType: string,
  title: string,
): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      const activeSheet = ctx.workbook.worksheets.getActiveWorksheet();
      activeSheet.load('name');
      await ctx.sync();

      // Create a temporary hidden sheet for the chart source data
      const tmpSheetName = `_ChartData_${Date.now()}`;
      const tmpSheet = ctx.workbook.worksheets.add(tmpSheetName);

      // Write the data (includes header row)
      const rowCount = data.length;
      const colCount = Math.max(...data.map((r) => Array.isArray(r) ? r.length : 0));
      if (rowCount < 2 || colCount < 1) {
        throw new Error('Pas assez de donnees pour creer un graphique.');
      }

      const startCell = tmpSheet.getRange('A1');
      const dataRange = startCell.getResizedRange(rowCount - 1, colCount - 1);
      dataRange.values = data.map((row) => {
        const r = row as unknown[];
        const padded: unknown[] = [];
        for (let i = 0; i < colCount; i++) {
          padded.push(r[i] ?? '');
        }
        return padded;
      });
      await ctx.sync();

      // Create chart on the active sheet using the temp data range
      const fullAddress = `${tmpSheetName}!A1:${columnLetterFromIndex(colCount - 1)}${rowCount}`;
      const chartSourceRange = tmpSheet.getRange(`A1:${columnLetterFromIndex(colCount - 1)}${rowCount}`);
      const chart = activeSheet.charts.add(
        chartType as Excel.ChartType,
        chartSourceRange,
        Excel.ChartSeriesBy.auto,
      );

      chart.title.visible = true;
      chart.title.text = title;

      // Position chart below the current selection
      const selected = ctx.workbook.getSelectedRange();
      selected.load(['rowCount', 'columnCount']);
      await ctx.sync();

      const posCell = selected.getCell(0, 0).getOffsetRange(0, selected.columnCount + 1);
      chart.setPosition(posCell);

      // Make the chart larger for readability
      chart.height = 320;
      chart.width = 480;

      await ctx.sync();

      // Clean up: hide the temp sheet (don't delete — chart still references it)
      tmpSheet.visibility = Excel.SheetVisibility.veryHidden;
      await ctx.sync();
    });
  } catch (err) {
    throw toWriterError(err, 'creer le graphique filtre');
  }
}

function columnLetterFromIndex(idx: number): string {
  let result = '';
  let n = idx;
  while (n >= 0) {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

// ─── Error translation ───────────────────────────────────

/**
 * Translate an Office.js error (or any error) into a clean user-facing Error.
 * If the error is already a user-facing Error from within our Excel.run callback,
 * pass it through unchanged.
 */
function toWriterError(err: unknown, action: string): Error {
  // Errors we threw ourselves inside Excel.run
  if (err instanceof Error && !(err as { code?: string }).code) {
    return err;
  }

  // OfficeExtension.Error
  if (isOfficeError(err)) {
    const mapped = mapOfficeErrorCode(err.code, action);
    if (mapped) return new Error(mapped);
    return new Error(`Erreur Excel lors de ${action} (code: ${err.code}).`);
  }

  // Unknown
  if (err instanceof Error) {
    return new Error(`Erreur inattendue lors de ${action}: ${err.message}`);
  }
  return new Error(`Erreur inattendue lors de ${action}.`);
}

interface OfficeError {
  code: string;
  message: string;
}

function isOfficeError(err: unknown): err is OfficeError {
  return (
    typeof err === 'object' &&
    err !== null &&
    typeof (err as OfficeError).code === 'string' &&
    typeof (err as OfficeError).message === 'string'
  );
}

function mapOfficeErrorCode(code: string, action: string): string | null {
  switch (code) {
    case 'InvalidReference':
      return `Reference invalide lors de ${action}. Verifiez la selection.`;
    case 'ItemNotFound':
      return `Cellule introuvable lors de ${action}. La feuille a-t-elle ete modifiee ?`;
    case 'InvalidArgument':
      return `Argument invalide lors de ${action}. Verifiez les donnees.`;
    case 'AccessDenied':
      return `Acces refuse lors de ${action}. Le classeur est peut-etre protege.`;
    case 'GeneralException':
      return `Erreur generale Excel lors de ${action}. Reessayez.`;
    default:
      return null;
  }
}
